/**
 * gameReducer.js
 * State machine for the natal chart RPG game.
 * Used via useReducer(gameReducer, initialState) in AntikytheraDevice.jsx.
 */

import { longitudeToSign, isNearBoundary, getAllAspects } from './chartMath';

// ─── PLANETS ─────────────────────────────────────────────────────────────────

export const PLANETS = [
  { id: 'sun',       glyph: '☉', color: '#ffbd2e', label: 'Sun'     },
  { id: 'moon',      glyph: '☽', color: '#8c78ff', label: 'Moon'    },
  { id: 'ascendant', glyph: '↑', color: '#00ff41', label: 'ASC'     },
  { id: 'mars',      glyph: '♂', color: '#ff5f56', label: 'Mars'    },
  { id: 'venus',     glyph: '♀', color: '#44aa88', label: 'Venus'   },
  { id: 'saturn',    glyph: '♄', color: '#888888', label: 'Saturn'  },
  { id: 'jupiter',   glyph: '♃', color: '#c4a44e', label: 'Jupiter' },
];

// ─── GEAR_STATES ─────────────────────────────────────────────────────────────

export const GEAR_STATES = [
  'intro',
  'boot',
  'ask-birthday',
  'ask-time',
  'ask-location',
  'riddle-mars',
  'riddle-venus',
  'riddle-saturn',
  'riddle-jupiter',
  'complete',
  'scoring',
  'export',
  'explore',
];

// ─── initialState ─────────────────────────────────────────────────────────────

export const initialState = {
  phase: 'intro',
  gearsUnlocked: 0,
  birthDate: null,
  birthTime: null,
  birthLocation: null,
  chartData: null,
  placedPlanets: [],
  aspects: [],
  messages: [],
  currentRiddle: null,
  riddleHintIndex: {},
  timeApproximate: false,
  selectedPlanet: null,
  chaoticGoodScore: null,
};

// ─── Riddle sequence ──────────────────────────────────────────────────────────

const RIDDLE_SEQUENCE = ['mars', 'venus', 'saturn', 'jupiter'];

/**
 * Returns the next riddle key after currentRiddle, or null if complete.
 * @param {string|null} currentRiddle
 * @returns {string|null}
 */
export function getNextRiddle(currentRiddle) {
  if (currentRiddle === null) return RIDDLE_SEQUENCE[0];
  const idx = RIDDLE_SEQUENCE.indexOf(currentRiddle);
  if (idx === -1 || idx === RIDDLE_SEQUENCE.length - 1) return null;
  return RIDDLE_SEQUENCE[idx + 1];
}

// ─── gameReducer ─────────────────────────────────────────────────────────────

/**
 * @param {typeof initialState} state
 * @param {{ type: string, payload?: any }} action
 * @returns {typeof initialState}
 */
export function gameReducer(state, action) {
  switch (action.type) {

    case 'ADD_MESSAGES':
      return {
        ...state,
        messages: [...state.messages, ...action.payload],
      };

    case 'SET_PHASE':
      return {
        ...state,
        phase: action.payload,
      };

    case 'SET_BIRTH_DATE':
      return {
        ...state,
        birthDate: action.payload,
      };

    case 'SET_BIRTH_TIME':
      return {
        ...state,
        birthTime: action.payload.time,
        timeApproximate: action.payload.approximate ?? false,
      };

    case 'SET_BIRTH_LOCATION':
      return {
        ...state,
        birthLocation: action.payload,
      };

    case 'SET_CHART_DATA':
      return {
        ...state,
        chartData: action.payload,
      };

    case 'PLACE_PLANET': {
      const { id, longitude } = action.payload;

      // Look up planet config for glyph, color, label
      const config = PLANETS.find(p => p.id === id);

      // Compute sign info from longitude
      const sign = longitudeToSign(longitude);
      const nearBoundary = isNearBoundary(longitude);

      const newPlanet = {
        id,
        longitude,
        sign,
        nearBoundary,
        glyph: config?.glyph ?? '?',
        color: config?.color ?? '#00ff41',
        label: config?.label ?? id,
      };

      // Avoid duplicates — replace if already placed
      const existing = state.placedPlanets.filter(p => p.id !== id);
      const updatedPlanets = [...existing, newPlanet];

      // Recompute all aspects across the new planet set
      const aspects = getAllAspects(updatedPlanets);

      return {
        ...state,
        placedPlanets: updatedPlanets,
        aspects,
        gearsUnlocked: state.gearsUnlocked + 1,
      };
    }

    case 'SET_CURRENT_RIDDLE':
      return {
        ...state,
        currentRiddle: action.payload,
      };

    case 'INCREMENT_HINT': {
      const riddleKey = action.payload;
      const current = state.riddleHintIndex[riddleKey] ?? 0;
      return {
        ...state,
        riddleHintIndex: {
          ...state.riddleHintIndex,
          [riddleKey]: current + 1,
        },
      };
    }

    case 'SET_CHAOTIC_GOOD_SCORE':
      return {
        ...state,
        chaoticGoodScore: action.payload,
      };

    case 'SELECT_PLANET':
      return {
        ...state,
        selectedPlanet: action.payload,
      };

    case 'RESET':
      return { ...initialState };

    default:
      return state;
  }
}
